import { useState } from 'react';
import { Menu, Users, Server, AlertTriangle, Activity, Trash2, Settings, LogOut, Home, Zap, BarChart3, Code2, ShieldAlert, X, Monitor } from 'lucide-react';
import apiClient, {authAPI} from '../Intercepter/APiClient';

export default function Sidebar({ activeTab, setActiveTab }) {
  const [collapsed, setCollapsed] = useState(false);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    authAPI.logout();
  };

  return (
    <div className={`bg-gradient-to-b from-slate-900 via-slate-900 to-black text-white transition-all duration-300 flex flex-col border-r border-white/10 relative ${collapsed ? 'w-20' : 'w-72'}`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between backdrop-blur-lg border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-2 rounded-lg">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <h2 className="font-black text-lg bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">CheckScript</h2>
              <p className="text-xs text-gray-400">Admin Console</p>
            </div>
          </div>
        )}
        <button 
          onClick={toggleSidebar} 
          className="p-2 rounded-lg hover:bg-white/10 transition-all duration-300"
        >
          {collapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="mt-6 flex flex-col flex-grow px-3 gap-2 overflow-y-auto">
        <div className="space-y-1">
          <p className={`text-xs font-bold text-gray-500 uppercase tracking-wider px-3 mb-3 transition-all ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
            Main
          </p>
          <SidebarItem 
            icon={<Home size={20} />} 
            title="Overview" 
            collapsed={collapsed} 
            active={activeTab === 'overview'} 
            onClick={() => window.location.href = '/console'} 
          />
          <SidebarItem 
            icon={<Activity size={20} />} 
            title="API Requests" 
            collapsed={collapsed} 
            active={activeTab === 'requests'} 
            onClick={() => window.location.href = '/api-requests'} 
          />
          <SidebarItem 
            icon={<Monitor size={20} />} 
            title="Remote Stats" 
            collapsed={collapsed} 
            active={activeTab === 'remote/stats'} 
            onClick={() => window.location.href = '/remote/stats'} 
          />
        </div>

        <div className="space-y-1">
          <p className={`text-xs font-bold text-gray-500 uppercase tracking-wider px-3 mb-3 transition-all ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
            Management
          </p>
          <SidebarItem 
            icon={<Users size={20} />} 
            title="Users" 
            collapsed={collapsed} 
            active={activeTab === 'users'} 
            onClick={() => window.location.href = '/users'} 
          />
          <SidebarItem 
            icon={<Server size={20} />} 
            title="Servers" 
            collapsed={collapsed} 
            active={activeTab === 'servers'} 
            onClick={() => window.location.href = '/servers'} 
          />
          <SidebarItem 
            icon={<BarChart3 size={20} />} 
            title="Analytics" 
            collapsed={collapsed} 
            active={activeTab === 'analytics'} 
            onClick={() => window.location.href = '/analytics'} 
          />
          <SidebarItem 
            icon={<Trash2 size={20} />} 
            title="Deleted Users" 
            collapsed={collapsed} 
            active={activeTab === 'deletedUsers'} 
            onClick={() => window.location.href = '/users/deleted'} 
          />
        </div>

        <div className="space-y-1">
          <p className={`text-xs font-bold text-gray-500 uppercase tracking-wider px-3 mb-3 transition-all ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
            System
          </p>
          <SidebarItem 
            icon={<ShieldAlert size={20} />} 
            title="Error Logs" 
            collapsed={collapsed} 
            active={activeTab === 'errors'} 
            onClick={() => window.location.href = '/error-logs'} 
          />
          <SidebarItem 
            icon={<Settings size={20} />} 
            title="Settings" 
            collapsed={collapsed} 
            active={activeTab === 'settings'} 
            onClick={() => window.location.href = '/settings'} 
          />
        </div>

        {/* Logout - Bottom */}
        <div className="mt-auto pt-6 border-t border-white/10">
          <SidebarItem 
            icon={<LogOut size={20} />} 
            title="Logout" 
            collapsed={collapsed} 
            onClick={handleLogout}
            danger
          />
        </div>
      </nav>

      {/* Footer Stats - Collapsed view shows icon indicator */}
      {!collapsed && (
        <div className="p-4 border-t border-white/10 backdrop-blur-lg">
          <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-2">Status</p>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-green-400">System Active</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarItem({ icon, title, collapsed, active = false, onClick, danger = false }) {
  return (
    <div 
      onClick={onClick}
      className={`flex items-center gap-3 py-3 px-3 rounded-lg cursor-pointer transition-all duration-300 group relative overflow-hidden
        ${active 
          ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-500/50 shadow-lg shadow-cyan-500/20' 
          : danger
          ? 'text-gray-400 hover:bg-red-500/20 hover:text-red-300 hover:border hover:border-red-500/30'
          : 'text-gray-400 hover:bg-white/5 hover:text-white hover:border hover:border-white/10'
        }`}
    >
      {/* Background animation on hover */}
      <div className={`absolute inset-0 transition-all duration-300 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></div>
      
      {/* Icon */}
      <div className="relative z-10 flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
        {icon}
      </div>

      {/* Title */}
      {!collapsed && (
        <span className="relative z-10 font-medium whitespace-nowrap transition-all duration-300">
          {title}
        </span>
      )}

      {/* Active indicator dot */}
      {active && !collapsed && (
        <div className="relative z-10 ml-auto w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
      )}

      {/* Tooltip for collapsed view */}
      {collapsed && (
        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none border border-white/10 z-50">
          {title}
        </div>
      )}
    </div>
  );
}